import { Icon } from '@synergy/icons';
import { Button, Flex } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Theme from "../../../styles/Theme/Theme";
import { Spinner, Text, SelectAtomic } from "@synergy/atomics_and_hooks";
import { useGlobalFilterContext } from '@synergy/microfrontend_core';
import { FilterCheckboxItem } from '../../Global/Atomics/FilterCheckboxItem';
import { FilterExpandedSection } from '../../Global/Molecules/FilterExpandedSection';

export const Filters = () => {
    const { mapViewType } = useGlobalFilterContext();
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(true);

    const [pointsOfInterest, setPointsOfInterest] = useState({
        incidents: true,
        services: false,
    });
    const [expandedSections, setExpandedSections] = useState({
        incidents: false,
        services: false,
    });

    const [closingSections, setClosingSections] = useState({
        incidents: false,
        services: false,
    });
    
    const [hasInitialFiltersApplied, setHasInitialFiltersApplied] = useState(false);

    const {
        dropdownEntity,
        entity,
        selectEntity,
        dropdownSubEntity,
        subEntity,
        setSubEntity,
        setIsToApplyFilters,

        mapItemsIncidentsData,
        mapItemsServicesData,

        // Incidents/Services classes select/setSelected/AllAvailable
        classesIncidents,
        setClassesIncidents,
        classesServices,
        setClassesServices,

        dropdownClassesIncidents,
        dropdownClassesServices,

        // Incidents priorities select/setSelected/AllAvailable
        prioritiesSelectedIncidents,
        setPrioritiesSelectedIncidents,
        prioritiesDropdownIncidents,

        // Incidents/Services critical infrastructure filter
        incidentsCriticalInfrastructure,
        setIncidentsCriticalInfrastructure,
        servicesCriticalInfrastructure,
        setServicesCriticalInfrastructure,

        combinedFilteredPOIs,
        setCombinedFilteredPOIs,
        activeLayerFilters,
        setActiveLayerFilters,

        // Refetch functions for Refresh button (new data from backend)
        refetchMapItemsIncidents,
        refetchMapItemsServices,
    } = useGlobalFilterContext();

    const visibleDropdownSubEntity = dropdownSubEntity?.filter(option => (
        option?.value !== "__all_municipalities__" && !option?.isAllMunicipalities
    ));
    const selectedMunicipalityValue = visibleDropdownSubEntity?.some(option => (
        option?.value === subEntity?.value
    ))
        ? subEntity?.value
        : visibleDropdownSubEntity?.[0]?.value;
    
    const incidentsData = mapItemsIncidentsData?.filter(item => item.latitude && item.latitude !== "0" && item.longitude && item.longitude !== "0")
    const incidentMediumHighSeverity = incidentsData?.filter(item => item.priority.includes("2 - Medium High"))
    const incidentsHigh = incidentsData?.filter(item => item.priority.includes("1 - High"));

    // Only include services that have classes existing in dropdownClassesServices
    const validServiceClasses = dropdownClassesServices?.map(c => c.value) || [];
    const servicesData = mapItemsServicesData?.filter(item => 
        item.latitude && 
        item.latitude !== "0" && 
        item.longitude && 
        item.longitude !== "0" &&
        validServiceClasses.includes(item.class)
    );

    // When entity/subEntity changes, reset all local filter state back to defaults.
    // Only Incidents is checked by default; all sections collapsed.
    // The auto-apply logic will re-trigger once fresh data arrives from the backend.
    useEffect(() => {
        setHasInitialFiltersApplied(false);

        // Reset checkboxes to default — only Incidents selected
        setPointsOfInterest({
            incidents: true,
            services: false,
        });

        // Collapse all expanded sections
        setExpandedSections({
            incidents: false,
            services: false,
        });
        setClosingSections({
            incidents: false,
            services: false,
        });
    }, [entity, subEntity]);

    // Loading states — data is null/undefined until the first backend response arrives
    const isIncidentsLoading = !mapItemsIncidentsData;
    const isServicesLoading = !mapItemsServicesData;

    // Initialize incidents filters with all options selected by default
    useEffect(() => {
        if (dropdownClassesIncidents && (!classesIncidents || classesIncidents.length === 0)) {
            setClassesIncidents(dropdownClassesIncidents);
        }
    }, [dropdownClassesIncidents]);

    useEffect(() => {
        if (prioritiesDropdownIncidents && (!prioritiesSelectedIncidents || prioritiesSelectedIncidents.length === 0)) {
            setPrioritiesSelectedIncidents(prioritiesDropdownIncidents);
        }
    }, [prioritiesDropdownIncidents]);

    // Auto-apply filters on initial load when incidents data is ready
    useEffect(() => {
        if (
            !hasInitialFiltersApplied &&
            incidentsData && 
            classesIncidents?.length > 0 && 
            prioritiesSelectedIncidents?.length > 0
        ) {
            // Trigger initial filter application
            onApplyFilters();
            setHasInitialFiltersApplied(true);
        }
    }, [incidentsData, classesIncidents, prioritiesSelectedIncidents, hasInitialFiltersApplied]);

    const onApplyFilters = () => {
        // Build combined filtered POIs based on selected checkboxes
        let combinedPOIs = [];

        // Filter and add Incidents if selected
        if (pointsOfInterest.incidents) {
            let filteredIncidents = incidentsData || [];
            
            // Apply classes filter if any selected
            if (classesIncidents && classesIncidents.length > 0) {
                filteredIncidents = filteredIncidents.filter(item => 
                    classesIncidents.map(c => c.value).includes(item.class)
                );
            }
            
            // Apply priorities filter if any selected
            if (prioritiesSelectedIncidents && prioritiesSelectedIncidents.length > 0) {
                filteredIncidents = filteredIncidents.filter(item => 
                    prioritiesSelectedIncidents.map(p => p.value).includes(item.priority)
                );
            }
            
            // Apply critical infrastructure filter (only keep truly critical items)
            if (incidentsCriticalInfrastructure) {
                filteredIncidents = filteredIncidents.filter(item => !!item.isCritical);
            }
            
            // Add layer type identifier
            filteredIncidents = filteredIncidents.map(item => ({
                ...item,
                layerType: 'Incidents'
            }));
            
            combinedPOIs = [...combinedPOIs, ...filteredIncidents];
        }

        // Filter and add Services if selected
        if (pointsOfInterest.services) {
            // Only show services if at least one class is selected
            if (classesServices && classesServices.length > 0) {
                let filteredServices = servicesData || [];
                
                // Apply classes filter
                filteredServices = filteredServices.filter(item => 
                    classesServices.map(c => c.value).includes(item.class)
                );
                
                // Apply critical infrastructure filter (only keep truly critical items)
                if (servicesCriticalInfrastructure) {
                    filteredServices = filteredServices.filter(item => !!item.isCritical);
                }
                
                // Add layer type identifier
                filteredServices = filteredServices.map(item => ({
                    ...item,
                    layerType: 'Services'
                }));
                
                combinedPOIs = [...combinedPOIs, ...filteredServices];
            }
            // If services checkbox is checked but no classes selected, add nothing (empty)
        }

        // Pass combined POIs to context
        if (setCombinedFilteredPOIs) {
            setCombinedFilteredPOIs(combinedPOIs);
        }

        // Pass active layer filters info for backward compatibility
        if (setActiveLayerFilters) {
            setActiveLayerFilters({
                layers: Object.keys(pointsOfInterest).filter(key => pointsOfInterest[key]),
                filters: {
                    incidents: {
                        classes: classesIncidents,
                        priorities: prioritiesSelectedIncidents,
                        criticalInfrastructure: incidentsCriticalInfrastructure
                    },
                    services: {
                        classes: classesServices,
                        criticalInfrastructure: servicesCriticalInfrastructure
                    }
                }
            });
        }

        // No longer triggers backend refetch — Apply only filters data already in memory
        // Use the "Refresh" button to fetch fresh data from backend
    };

    const handlePointOfInterestChange = (key) => {
        setPointsOfInterest(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleSection = (section) => {
        if (expandedSections[section]) {
            setClosingSections(prev => ({ ...prev, [section]: true }));
            setTimeout(() => {
                setExpandedSections(prev => ({ ...prev, [section]: false }));
                setClosingSections(prev => ({ ...prev, [section]: false }));
            }, 200);
        } else {
            setExpandedSections(prev => ({ ...prev, [section]: true }));
        }
    };

    const handleIncidentsClassesChange = (classes) => {
        setClassesIncidents(classes);
    };

    const handleIncidentsPrioritiesChange = (priorities) => {
        setPrioritiesSelectedIncidents(priorities);
    };

    const handleServicesClassesChange = (classes) => {
        setClassesServices(classes);
    };

    // Refresh: fetches fresh data from the backend for all layers,
    // then re-applies the current filters automatically
    const [isRefreshing, setIsRefreshing] = useState(false);

    const onRefresh = async () => {
        setIsRefreshing(true);
        try {
            // Trigger all backend refetches in parallel
            await Promise.all([
                refetchMapItemsIncidents?.(),
                refetchMapItemsServices?.(),
            ]);
        } catch (error) {
            console.error("Error refreshing data:", error);
        } finally {
            setIsRefreshing(false);
        }
    };

    // After backend data refreshes, re-apply filters automatically so the map updates
    const prevIncidentsRef = useRef(mapItemsIncidentsData);
    const prevServicesRef = useRef(mapItemsServicesData);

    useEffect(() => {
        const dataChanged =
            prevIncidentsRef.current !== mapItemsIncidentsData ||
            prevServicesRef.current !== mapItemsServicesData;

        if (dataChanged && hasInitialFiltersApplied) {
            prevIncidentsRef.current = mapItemsIncidentsData;
            prevServicesRef.current = mapItemsServicesData;
            // Re-apply filters with the fresh data
            onApplyFilters();
        }
    }, [mapItemsIncidentsData, mapItemsServicesData]);

    useEffect(()=> {
        if(mapViewType !== 'map'){
            setIsOpen(false);
        }
    }, [mapViewType]);

    return (
        <FiltersWrapper className={isOpen ? " is-open" : null}>
            <div className="filters-header">
                <Button
                    className="filters-button"
                    onClick={() => { setIsOpen(!isOpen) }}
                    type="text"
                    icon={
                        <Icon iconsize={'sm'} icon={isOpen === true ? 'Collapse' : 'CollapseOpen'} color={'blue-4'} />
                    }
                    disabled={mapViewType !== 'map'}
                />
                {isOpen ?
                    <Text textcolor='coolgray-12' marginleft='s03' textsize='text_sm' textweight='regular' $alignItems='center'>{t('filters')}</Text> : null
                }
            </div>
            {isOpen ?
                <div className="filter-content">
                    <div className="filter-content-item">
                        {dropdownEntity ?
                            <SelectAtomic optionsData={dropdownEntity} handleOptionsChange={(value, record) => { selectEntity(record) }} defaultValue={entity?.value} fullWidth={true} />
                            : <Flex justify="center"><Spinner stroke="green" size='25px'/></Flex>
                        }
                    </div>

                    {visibleDropdownSubEntity?.length > 0 ?
                        <div className="filter-content-item">
                            {dropdownEntity ?
                                <SelectAtomic optionsData={visibleDropdownSubEntity} handleOptionsChange={(value, record) => { setSubEntity(record) }} defaultValue={selectedMunicipalityValue} fullWidth={true} />
                                : <Flex justify="center"><Spinner stroke="green" size='25px' /></Flex>
                            }
                        </div>
                        : null}

                    <div className="points-of-interest-section" style={{ background: `${Theme.color['neutral-3']}` }}>
                        <div className="checkbox-list">
                            <div className="checkbox-list-header" >
                                <Text textcolor='coolgray-12' textsize='text_sm' textweight='semibold'>
                                    {t('pointsOfInterest') || 'Pontos de Interesse'}
                                </Text>
                            </div>

                            {/* Incidents */}
                            <FilterCheckboxItem
                                checked={pointsOfInterest.incidents}
                                onChange={() => handlePointOfInterestChange('incidents')}
                                label={t('incidents') || 'Incidents'}
                                expanded={expandedSections.incidents}
                                onToggle={() => !isIncidentsLoading && toggleSection('incidents')}
                                loading={isIncidentsLoading}
                                badges={[
                                    { count: incidentsHigh?.length, variant: 'red' },
                                    { count: incidentMediumHighSeverity?.length, variant: 'orange' },
                                ]}
                            />
                            {expandedSections.incidents && !isIncidentsLoading && (
                                <FilterExpandedSection
                                    key="incidents-section"
                                    className={closingSections.incidents ? 'closing' : ''}
                                    dropdowns={[
                                        {
                                            optionsData: dropdownClassesIncidents,
                                            handleChange: handleIncidentsClassesChange,
                                            defaultValue: classesIncidents || [],
                                            placeholder: t('selectClasses') || 'Select Classes',
                                        },
                                        {
                                            optionsData: prioritiesDropdownIncidents,
                                            handleChange: handleIncidentsPrioritiesChange,
                                            defaultValue: prioritiesSelectedIncidents || [],
                                            placeholder: t('selectPriorities') || 'Select Priorities',
                                        },
                                    ]}
                                    switchProps={{
                                        checked: incidentsCriticalInfrastructure,
                                        onChange: setIncidentsCriticalInfrastructure,
                                        label: t('criticalInfrastructure') || 'Infraestruturas críticas',
                                        tooltip: t('criticalInfrastructureTooltip') || 'Os POIs de infraestruturas críticas ficam destacados com um círculo no mapa',
                                    }}
                                />
                            )}

                            {/* Services */}
                            <FilterCheckboxItem
                                checked={pointsOfInterest.services}
                                onChange={() => handlePointOfInterestChange('services')}
                                label={t('services') || 'Serviços'}
                                expanded={expandedSections.services}
                                onToggle={() => !isServicesLoading && toggleSection('services')}
                                loading={isServicesLoading}
                                badges={[
                                    { count: servicesData?.length, variant: 'blue' },
                                ]}
                            />
                            {expandedSections.services && !isServicesLoading && (
                                <FilterExpandedSection
                                    key="services-section"
                                    className={closingSections.services ? 'closing' : ''}
                                    dropdowns={[
                                        {
                                            optionsData: dropdownClassesServices,
                                            handleChange: handleServicesClassesChange,
                                            defaultValue: classesServices || [],
                                            placeholder: t('selectClasses') || 'Select Classes',
                                        },
                                    ]}
                                    switchProps={{
                                        checked: servicesCriticalInfrastructure,
                                        onChange: setServicesCriticalInfrastructure,
                                        label: t('criticalInfrastructure') || 'Infraestruturas críticas',
                                        tooltip: t('criticalInfrastructureTooltip') || 'Os POIs de infraestruturas críticas ficam destacados com um círculo no mapa',
                                    }}
                                />
                            )}

                        </div>
                    </div>

                    <Flex gap='middle' vertical="true" style={{ marginBottom: '24px' }}>
                        <Button shape="round" className="ant-btn-blue -small" onClick={() => onApplyFilters()}>{t('apply')}</Button>
                        <Button 
                            shape="round" 
                            className="-small" 
                            onClick={() => onRefresh()} 
                            loading={isRefreshing}
                            style={{ 
                                backgroundColor: Theme.color['coolgray-2'], 
                                borderColor: Theme.color['coolgray-4'], 
                                color: Theme.color['coolgray-12'] 
                            }}
                        >
                            {t('refresh') || 'Refresh'}
                        </Button>
                    </Flex>
                </div>
                : null
            }
        </FiltersWrapper >
    )
}

const FiltersWrapper = styled.div.attrs({
    className: 'with-bg-color',
})`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${Theme.color['neutral-2']};
    padding: ${Theme.spacing['s07']} ${Theme.spacing['s05']};
    width: auto;
    height: -webkit-fill-available; /* Doesnt work in all browsers */
    height: 100%; /* works in firefox and others browseres */

    &.is-open {
        width: 234px;
    }

    .filters-header{
        display: flex;
        margin-bottom: ${Theme.spacing['s07']};

        .filters-title{
            display: flex;
            align-items: center;
        }
        .filters-button{
            cursor: pointer;
        }
    };

    .filter-content{
        overflow-y: auto;
        height: 100%;
        padding-bottom: ${Theme.spacing['s07']};

        &::-webkit-scrollbar {
            -webkit-appearance: none;
            width: 2px;
        }

        &::-webkit-scrollbar-thumb {
            background: ${Theme.color['coolgray-2']};
            border-radius: 10px;
        }
        
        .filter-content-item{
            margin-bottom: ${Theme.spacing['s07']};

            .antdSelect{
                width: 100%;

                .ant-select-arrow{
                    color: ${Theme.color['blue-4']};
                    font-size: unset;
                }

                .ant-select-selector{
                    min-height: 48px;
                    border: none;

                    .ant-select-selection-placeholder{//if mode is none need this property to center
                        align-self: center;
                    }

                    .ant-select-selection-item{//if mode is none need this property to center
                        align-self: center;
                    }
                }
            }

            .ant-picker{
                width: 100%;
                height: 48px;
                border: none;

                .ant-picker-suffix{
                    color: ${Theme.color['blue-4']};
                }
            }
        }

        .submit-content{
            display: flex;
            flex-direction: column;
            padding: 8px 0px 0px 0px;//Top padding 32px - s07 = 8px;
            
            .buttonFilter{
                border-radius: 20px;
                cursor: pointer;
                font-size: ${Theme.typography.fontSize['text_sm_desktop']};
            }
            
            .saveFilter{
                background-color: transparent;
                height: 40px;
                border-color: ${Theme.color['coolgray-2']};
                border-style: solid;
                margin-bottom: ${Theme.spacing['s05']};

                &:hover{
                    border-color: ${Theme.color['black-1']};
                    /* background-color: ${Theme.color['coolgray-4']}; */
                }

                &:focus{
                    color: ${Theme.color['black-1']};
                    background-color: ${Theme.color['white']};
                    border-color: ${Theme.color['blue-2']};
                }

                &:disabled{
                    color: ${Theme.color['black-1']};
                    background-color: #F8F8F8;
                    cursor: not-allowed;
                    border-color: ${Theme.color['coolgray-2']};
                }
            }

            .whiteButton{
                &:disabled{
                    color: ${Theme.color['coolgray-11']};
                    background-color: ${Theme.color['coolgray-2']};
                    cursor: not-allowed;
                }
            }
        }

        .points-of-interest-section {
            margin-top: ${Theme.spacing['s05']};
            margin-bottom: ${Theme.spacing['s05']};
            padding: 10px 8px 8px;
            border-radius: 8px;

            .checkbox-list {
                display: flex;
                flex-direction: column;
                gap: ${Theme.spacing['s05']};

                .checkbox-list-header {
                    margin-bottom: ${Theme.spacing['s03']};
                    color: ${Theme.color['coolgray-6']};
                    font-size: ${Theme.typography.fontSize['text_sm_desktop']};
                }
            }
        }
    }
`;